import { defineStore } from 'pinia'
import _ from 'lodash'
import { BlockBlobClient } from '@azure/storage-blob'

import { resourceApi } from './common/api'
import { rules, defaultItemsPerPage } from './common/commonFunctions'
import { createIndexedDBAdapter } from './common/indexedDbStorage'

const simulationApi = resourceApi.extend('simulationData')
const profilesApi = resourceApi.extend('simulationProfiles')
const conceptsApi = resourceApi.extend('simulationConcepts')
const blobApi = resourceApi.extend('blob')
const sas2dApi = resourceApi.extend('sas2d')

const heroIndexedDb = createIndexedDBAdapter('simulation', 'hero')

export const useSimulation = defineStore('simulation', {
  persist: [
    {
      key: (context) => `${__APP_NAME__}_${context}`,
      pick: ['itemsPerPage'],
      storage: localStorage
    }
  ],

  state() {
    return {
      rules: rules.simulation,
      isLoadingActiveSimulations: false,
      errorActiveSimulations: null,
      statusActiveSimulations: null,
      isLoadingArchivedSimulations: false,
      errorArchivedSimulations: null,
      statusArchivedSimulations: null,
      isLoadingArchivingSimulations: false,
      errorArchivingSimulations: null,
      statusArchivingSimulations: null,
      isLoadingRestoringSimulations: false,
      errorRestoringSimulations: null,
      statusRestoringSimulations: null,
      isLoadingEditingSimulation: false,
      errorEditingSimulation: null,
      statusEditingSimulation: null,
      isLoadingCreatingSimulation: false,
      errorCreatingSimulation: null,
      statusCreatingSimulation: null,
      errorHeroImage: null,
      statusHeroImage: null,
      isLoadingHeroImage: false,
      isActionMultiple: {},
      activeSimulations: null,
      archivedSimulations: null,
      fetchSimulationsFrequency: 300000, // 5 minutes
      fetchActiveSimulationsInterval: null,
      fetchArchivedSimulationsInterval: null,
      adminTabIndex: null,
      adminTableData: null,
      isViewSimulation: false,
      isEditSimulation: false,
      isEdit: false,
      isArchive: false,
      isRestore: false,
      editSimulation: null,
      viewSimulation: null,
      showProfile: false,
      showHero: false,
      showDialog: false,
      selectedSimulationIds: null,
      query2D: null,
      player2DBaseUrl: null,
      itemsPerPage: {
        active: defaultItemsPerPage,
        archived: defaultItemsPerPage
      },
      profiles: [
        {
          code: 'name',
          label: 'Name',
          type: 'text',
          hint: 'The Name must be at least 3 characters long.'
        },
        {
          code: 'dob',
          label: 'Date of Birth (MM-DD)',
          type: {
            mask: 'MM-DD'
          },
          hint: 'The Date of Birth must be a valid MM-DD combination.'
        },
        {
          code: 'age',
          label: 'Age',
          type: 'numbers',
          numbers: [
            {
              code: 'years',
              label: 'Years',
              min: 1,
              hint: 'The Age in Years must be between 1 and 120.',
              prefix: 'y'
            },
            {
              code: 'months',
              label: 'Months',
              min: 1,
              hint: 'The Age in Months must be between 1 and 36.',
              prefix: 'm'
            },
            {
              code: 'weeks',
              label: 'Weeks',
              min: 0,
              hint: 'The Age in Weeks must be between 0 and 100.',
              prefix: 'w'
            },
            {
              code: 'days',
              label: 'Days',
              min: 0,
              hint: 'The Age in Days must be between 0 and 100.',
              prefix: 'd'
            }
          ]
        },
        {
          code: 'weight',
          label: 'Weight',
          type: 'number',
          min: 1,
          step: 1,
          precision: 0,
          hint: 'The Weight must be greater than 0 kilograms.',
          prefix: 'kg'
        },
        {
          code: 'height',
          label: 'Height',
          type: 'number',
          min: 1,
          step: 1,
          precision: 0,
          hint: 'The Height must be greater than 0 centimeters.',
          prefix: 'cm'
        },
        {
          code: 'sex',
          label: 'Sex Assigned at Birth',
          type: 'select',
          items: ['Female', 'Male']
        },
        {
          code: 'gender',
          label: 'Gender Identity',
          type: 'select',
          items: [
            'Female',
            'Male',
            'Non-Binary',
            'Transgender Female',
            'Transgender Male',
            'Non-conforming',
            'Prefer not to say'
          ]
        },
        {
          code: 'orientation',
          label: 'Sexual Orientation',
          type: 'select',
          items: [
            'Heterosexual',
            'Homosexual',
            'Bisexual',
            'Pansexual',
            'Asexual',
            'Demisexual',
            'Queer',
            'Questioning',
            'Androsexual',
            'Gynosexual',
            'Polysexual',
            'Graysexual',
            'Perfer not to say'
          ]
        },
        {
          code: 'marital',
          label: 'Marital Status',
          type: 'select',
          items: [
            'Single',
            'Married',
            'Divorced',
            'Widowed',
            'Separated',
            'Partnership'
          ]
        },
        {
          code: 'employment',
          label: 'Employment Status',
          type: 'select',
          items: [
            'Full-Time',
            'Part-Time',
            'Self-Employed',
            'Unemployed',
            'Retired',
            'Student',
            'Homemaker',
            'Disabled',
            'Military'
          ]
        },
        {
          code: 'insurance',
          label: 'Insurance Status',
          type: 'select',
          items: [
            'Private Insurance',
            'Medicare',
            'Medicaid',
            'Tricare (Military)',
            'Uninsured (Self-Pay)'
          ]
        },
        {
          code: 'veteran',
          label: 'Veteran Status',
          type: 'select',
          items: ['Non-Veteran', 'Veteran', 'Active Duty', 'Reserve/Guard']
        },
        {
          code: 'race',
          label: 'Racial Group',
          type: 'select',
          items: [
            'American Indian or Alaska Native',
            'Asian',
            'Black or African American',
            'Native Hawaiian or Other Pacific Islander',
            'White'
          ]
        },
        {
          code: 'conditions',
          label: 'Pre-existing Conditions',
          type: 'combobox',
          multiple: true,
          chips: true
        },
        {
          code: 'medications',
          label: 'Current Medications',
          type: 'combobox',
          multiple: true,
          chips: true
        },
        {
          code: 'hobbies',
          label: 'Hobbies',
          type: 'combobox',
          multiple: true,
          chips: true
        },
        {
          code: 'religion',
          label: 'Religion',
          type: 'text',
          hint: 'The Religion must be at least 2 characters long.'
        },
        {
          code: 'language',
          label: 'Primary Language',
          type: 'text',
          hint: 'The Primary Language must be at least 3 characters long.'
        },
        {
          code: 'history',
          label: 'Social History',
          type: 'textarea',
          autoGrow: true,
          counter: true,
          rows: 2,
          maxRows: 5,
          hint: 'The Social History must be between 5 and 500 characters long.'
        },
        {
          code: 'person',
          label: 'Support Person',
          type: 'text',
          hint: 'The Support Person must be at least 3 characters long.'
        }
      ],
      conceptsWithSubheaders: [
        {
          type: 'subheader',
          title: 'Clinical Skills',
          value: 'clinical_skills',
          color: 'pink',
          concepts: [
            {
              source: null,
              code: 'acid_base_balance',
              label: 'Acid Base Balance'
            },
            {
              source: null,
              code: 'clotting',
              label: 'Clotting'
            },
            {
              source: null,
              code: 'comfort',
              label: 'Comfort'
            },
            {
              source: null,
              code: 'cognition',
              label: 'Cognition'
            },
            {
              source: null,
              code: 'communication',
              label: 'Communication'
            }
          ]
        },
        {
          type: 'subheader',
          title: 'Patient Care & Ethics',
          value: 'patient_care_ethics',
          color: 'indigo',
          concepts: [
            {
              source: null,
              code: 'coping',
              label: 'Coping'
            },
            {
              source: null,
              code: 'diversity',
              label: 'Diversity'
            },
            {
              source: null,
              code: 'elimination',
              label: 'Elimination'
            },
            {
              source: null,
              code: 'end_of_life',
              label: 'End of Life'
            },
            {
              source: null,
              code: 'ethical_and_legal_practice',
              label: 'Ethical and Legal Practice'
            },
            {
              source: null,
              code: 'evidence-based_practice',
              label: 'Evidence-Based Practice'
            },
            {
              source: null,
              code: 'fluid_&_electrolyte_balance',
              label: 'Fluid & Electrolyte Balance'
            },
            {
              source: null,
              code: 'functional_ability',
              label: 'Functional Ability'
            },
            {
              source: null,
              code: 'gas_exchange',
              label: 'Gas Exchange'
            },
            {
              source: null,
              code: 'health_information_technology',
              label: 'Health Information Technology'
            },
            {
              source: null,
              code: 'health_promotion',
              label: 'Health Promotion'
            }
          ]
        },
        {
          type: 'subheader',
          title: 'Communication & Diversity',
          value: 'communication_diversity',
          color: 'lime',
          concepts: [
            {
              source: null,
              code: 'human_development',
              label: 'Human Development'
            },
            {
              source: null,
              code: 'immunity',
              label: 'Immunity'
            },
            {
              source: null,
              code: 'interpersonal_relationships',
              label: 'Interpersonal Relationships'
            },
            {
              source: null,
              code: 'intracranial_regulation',
              label: 'Intracranial Regulation'
            },
            {
              source: null,
              code: 'leadership_and_management',
              label: 'Leadership and Management'
            },
            {
              source: null,
              code: 'metabolism',
              label: 'Metabolism'
            },
            {
              source: null,
              code: 'mobility',
              label: 'Mobility'
            },
            {
              source: null,
              code: 'mood_affect',
              label: 'Mood Affect'
            },
            {
              source: null,
              code: 'nutrition',
              label: 'Nutrition'
            },
            {
              source: null,
              code: 'patient-centered_care',
              label: 'Patient-Centered Care'
            },
            {
              source: null,
              code: 'patient_education',
              label: 'Patient Education'
            },
            {
              source: null,
              code: 'perfusion',
              label: 'Perfusion'
            },
            {
              source: null,
              code: 'professionalism',
              label: 'Professionalism'
            },
            {
              source: null,
              code: 'quality_improvement',
              label: 'Quality Improvement'
            }
          ]
        },
        {
          type: 'subheader',
          title: 'Psychosocial & Cognitive',
          value: 'psychosocial_cognitive',
          color: 'cyan',
          concepts: [
            {
              source: null,
              code: 'reproduction',
              label: 'Reproduction'
            },
            {
              source: null,
              code: 'safety',
              label: 'Safety'
            }
          ]
        },
        {
          type: 'subheader',
          title: 'Clinical Fundamentals',
          value: 'clinical_fundamentals',
          color: 'deep-orange',
          concepts: [
            {
              source: null,
              code: 'sensory_perception',
              label: 'Sensory Perception'
            },
            {
              source: null,
              code: 'sexuality',
              label: 'Sexuality'
            },
            {
              source: null,
              code: 'sleep',
              label: 'Sleep'
            },
            {
              source: null,
              code: 'teamwork_&_collaboration',
              label: 'Teamwork & Collaboration'
            },
            {
              source: null,
              code: 'thermoregulation',
              label: 'Thermoregulation'
            },
            {
              source: null,
              code: 'tissue_integrity',
              label: 'Tissue Integrity'
            },
            {
              source: null,
              code: 'clinical_judgment',
              label: 'Clinical Judgment'
            },
            {
              source: null,
              code: 'healthcare_organizations',
              label: 'Healthcare Organizations'
            },
            {
              source: null,
              code: 'grief',
              label: 'Grief'
            },
            {
              source: null,
              code: 'health_policy',
              label: 'Health Policy'
            }
          ]
        }
      ],
      concepts: [
        {
          source: null,
          code: 'acid_base_balance',
          label: 'Acid Base Balance'
        },
        {
          source: null,
          code: 'clotting',
          label: 'Clotting'
        },
        {
          source: null,
          code: 'comfort',
          label: 'Comfort'
        },
        {
          source: null,
          code: 'cognition',
          label: 'Cognition'
        },
        {
          source: null,
          code: 'communication',
          label: 'Communication'
        },
        {
          source: null,
          code: 'coping',
          label: 'Coping'
        },
        {
          source: null,
          code: 'diversity',
          label: 'Diversity'
        },
        {
          source: null,
          code: 'elimination',
          label: 'Elimination'
        },
        {
          source: null,
          code: 'end_of_life',
          label: 'End of Life'
        },
        {
          source: null,
          code: 'ethical_and_legal_practice',
          label: 'Ethical and Legal Practice'
        },
        {
          source: null,
          code: 'evidence-based_practice',
          label: 'Evidence-Based Practice'
        },
        {
          source: null,
          code: 'fluid_&_electrolyte_balance',
          label: 'Fluid & Electrolyte Balance'
        },
        {
          source: null,
          code: 'functional_ability',
          label: 'Functional Ability'
        },
        {
          source: null,
          code: 'gas_exchange',
          label: 'Gas Exchange'
        },
        {
          source: null,
          code: 'health_information_technology',
          label: 'Health Information Technology'
        },
        {
          source: null,
          code: 'health_promotion',
          label: 'Health Promotion'
        },
        {
          source: null,
          code: 'human_development',
          label: 'Human Development'
        },
        {
          source: null,
          code: 'immunity',
          label: 'Immunity'
        },
        {
          source: null,
          code: 'interpersonal_relationships',
          label: 'Interpersonal Relationships'
        },
        {
          source: null,
          code: 'intracranial_regulation',
          label: 'Intracranial Regulation'
        },
        {
          source: null,
          code: 'leadership_and_management',
          label: 'Leadership and Management'
        },
        {
          source: null,
          code: 'metabolism',
          label: 'Metabolism'
        },
        {
          source: null,
          code: 'mobility',
          label: 'Mobility'
        },
        {
          source: null,
          code: 'mood_affect',
          label: 'Mood Affect'
        },
        {
          source: null,
          code: 'nutrition',
          label: 'Nutrition'
        },
        {
          source: null,
          code: 'patient-centered_care',
          label: 'Patient-Centered Care'
        },
        {
          source: null,
          code: 'patient_education',
          label: 'Patient Education'
        },
        {
          source: null,
          code: 'perfusion',
          label: 'Perfusion'
        },
        {
          source: null,
          code: 'professionalism',
          label: 'Professionalism'
        },
        {
          source: null,
          code: 'quality_improvement',
          label: 'Quality Improvement'
        },
        {
          source: null,
          code: 'reproduction',
          label: 'Reproduction'
        },
        {
          source: null,
          code: 'safety',
          label: 'Safety'
        },
        {
          source: null,
          code: 'sensory_perception',
          label: 'Sensory Perception'
        },
        {
          source: null,
          code: 'sexuality',
          label: 'Sexuality'
        },
        {
          source: null,
          code: 'sleep',
          label: 'Sleep'
        },
        {
          source: null,
          code: 'teamwork_&_collaboration',
          label: 'Teamwork & Collaboration'
        },
        {
          source: null,
          code: 'thermoregulation',
          label: 'Thermoregulation'
        },
        {
          source: null,
          code: 'tissue_integrity',
          label: 'Tissue Integrity'
        },
        {
          source: null,
          code: 'clinical_judgment',
          label: 'Clinical Judgment'
        },
        {
          source: null,
          code: 'healthcare_organizations',
          label: 'Healthcare Organizations'
        },
        {
          source: null,
          code: 'grief',
          label: 'Grief'
        },
        {
          source: null,
          code: 'health_policy',
          label: 'Health Policy'
        }
      ],
      defaultHeroUrl: 'logo_purple.png',
      heroImageLoaders: {},
      heroImageArchives: {},
      heroUploadProgress: null,
      simulationResources: null
    }
  },

  getters: {
    getAdminTabs: ({
      activeSimulations,
      archivedSimulations,
      itemsPerPage,
      defaultHeroUrl,
      heroImageLoaders
    }) => {
      const defaultHeaders = [
        {
          title: 'Title',
          key: 'title',
          value: 'title',
          headerProps: { style: { maxWidth: '150px' } },
          cellProps: { style: { maxWidth: '150px' } }
        },
        {
          title: 'Description',
          key: 'description',
          value: 'description',
          headerProps: { style: { maxWidth: '350px' } },
          cellProps: { style: { maxWidth: '350px' } }
        },
        {
          title: 'Estimated Duration',
          key: 'estimatedDuration',
          value: 'estimatedDuration'
        },
        {
          title: 'Author',
          key: 'author',
          value: 'author'
        },
        {
          title: 'Institution',
          key: 'institution',
          value: '_institution'
        }
      ]
      const filterKeys = _.map(defaultHeaders, (h) => h.key)
      const tabs = [
        {
          text: 'Active',
          value: 'active',
          itemsPerPage: itemsPerPage.active,
          count: activeSimulations?.length || null,
          headers: [
            ...defaultHeaders,
            {
              key: 'actions-active',
              sortable: false
            }
          ],
          data: activeSimulations?.length
            ? _.orderBy(
                _.map(activeSimulations, (s, i) => {
                  s._heroUrl =
                    s.heroImage == null ||
                    heroImageLoaders?.[s.id]?.error != null ||
                    heroImageLoaders?.[s.id]?.loading == true
                      ? defaultHeroUrl
                      : (s.heroUrl ?? null)
                  s._institution = s?.institution?.name
                  s.index = i
                  return s
                }),
                ['updateTs'],
                ['desc']
              )
            : [],
          filterKeys
        },
        {
          text: 'Archived',
          value: 'archived',
          itemsPerPage: itemsPerPage.archived,
          count: archivedSimulations?.length || null,
          headers: [
            ...defaultHeaders,
            {
              key: 'actions-archived',
              sortable: false
            }
          ],
          data: archivedSimulations?.length
            ? _.orderBy(
                _.map(archivedSimulations, (s, i) => {
                  s._heroUrl =
                    s.heroImage == null ||
                    heroImageLoaders?.[s.id]?.error != null ||
                    heroImageLoaders?.[s.id]?.loading == true
                      ? defaultHeroUrl
                      : (s.heroUrl ?? null)
                  s._institution = s?.institution?.name
                  s.index = i
                  return s
                }),
                ['deleteTs'],
                ['desc']
              )
            : [],
          filterKeys
        }
      ]
      return _.map(
        _.filter(tabs, (t, i) => i == 0 || t.count > 0),
        (t, i) => {
          t.index = i
          return t
        }
      )
    },
    getAdminTableData: ({ adminTableData }) => {
      return adminTableData
    },
    getFetchSimulationsFrequency: ({ fetchSimulationsFrequency }) => {
      return fetchSimulationsFrequency
    },
    getFetchActiveSimulationsInterval: ({ fetchActiveSimulationsInterval }) => {
      return fetchActiveSimulationsInterval
    },
    getFetchArchivedSimulationsInterval: ({
      fetchArchivedSimulationsInterval
    }) => {
      return fetchArchivedSimulationsInterval
    },
    getProfiles: ({ profiles }) => {
      return profiles.map((p, i) => {
        p.index = i
        return p
      })
    },
    getProfilesByCode: ({ profiles }) => {
      return _.keyBy(
        profiles.map((p, i) => {
          p.index = i
          return p
        }),
        'code'
      )
    },
    getConceptsWithSubheaders: ({ conceptsWithSubheaders }) => {
      return conceptsWithSubheaders.flatMap((subheader) => {
        const _concepts = subheader.concepts.map((concept) => ({
          ...concept,
          color: subheader.color,
          type: 'item'
        }))
        return [subheader, ..._concepts]
      })
    },
    getConcepts: ({ concepts }) => {
      return concepts
    },
    getViewSimulation: ({ viewSimulation }) => {
      return viewSimulation
    },
    getEditSimulation: ({ editSimulation }) => {
      return editSimulation
    },
    getShowProfile: ({ showProfile }) => {
      return showProfile
    },
    getShowHero: ({ showHero }) => {
      return showHero
    },
    getIsEditSimulation: ({ isEditSimulation }) => {
      return isEditSimulation
    },
    getMenuIcon: ({ isViewSimulation }) => {
      return isViewSimulation ? 'mdi-chevron-left' : 'mdi-television-play'
    },
    getIsViewSimulation: ({ isViewSimulation }) => {
      return isViewSimulation
    },
    getActionsActiveSimulation: ({ isViewSimulation, viewSimulation }) => {
      return isViewSimulation && viewSimulation?.deleteTs == null
    },
    getActionsArchivedSimulation: ({ isViewSimulation, viewSimulation }) => {
      return isViewSimulation && viewSimulation?.deleteTs != null
    },
    getIsListSimulations: ({ isViewSimulation }) => {
      return !isViewSimulation
    },
    getActiveSimulations: ({ activeSimulations }) => {
      return activeSimulations
    },
    getIsLoadingActiveSimulations: ({ isLoadingActiveSimulations }) => {
      return isLoadingActiveSimulations
    },
    getIsLoadingArchivedSimulations: ({ isLoadingArchivedSimulations }) => {
      return isLoadingArchivedSimulations
    },
    getIsLoadingArchivingSimulations: ({ isLoadingArchivingSimulations }) => {
      return isLoadingArchivingSimulations
    },
    getIsLoadingRestoringSimulations: ({ isLoadingRestoringSimulations }) => {
      return isLoadingRestoringSimulations
    },
    getIsLoadingEditingSimulation: ({ isLoadingEditingSimulation }) => {
      return isLoadingEditingSimulation
    },
    getIsLoadingCreatingSimulation: ({ isLoadingCreatingSimulation }) => {
      return isLoadingCreatingSimulation
    },
    getHeroUploadProgress: ({ heroUploadProgress }) => {
      return heroUploadProgress
    },
    getIsLoadingMessage: ({
      isLoadingArchivingSimulations,
      isLoadingRestoringSimulations,
      isLoadingEditingSimulation,
      isLoadingCreatingSimulation,
      isActionMultiple
    }) => {
      if (isLoadingArchivingSimulations)
        return _.isObjectLike(isActionMultiple) &&
          !_.isEmpty(isActionMultiple) &&
          _.has(isActionMultiple, 'archivingSimulations') &&
          isActionMultiple.archivingSimulations == true
          ? 'Archiving simulations...'
          : 'Archiving simulation...'
      else if (isLoadingRestoringSimulations)
        return _.isObjectLike(isActionMultiple) &&
          !_.isEmpty(isActionMultiple) &&
          _.has(isActionMultiple, 'restoringSimulations') &&
          isActionMultiple.restoringSimulations == true
          ? 'Restoring simulations...'
          : 'Restoring simulation...'
      else if (isLoadingEditingSimulation) return 'Updating simulation...'
      else if (isLoadingCreatingSimulation) return 'Creating simulation...'
      else return null
    },
    getErrors: ({
      errorActiveSimulations,
      errorArchivedSimulations,
      errorArchivingSimulations,
      errorRestoringSimulations,
      errorEditingSimulation,
      errorCreatingSimulation,
      errorHeroImage
    }) => {
      let returnValue = []
      if (errorActiveSimulations)
        returnValue.push({
          text: errorActiveSimulations,
          key: 'activeSimulations'
        })
      if (errorArchivedSimulations)
        returnValue.push({
          text: errorArchivedSimulations,
          key: 'archivedSimulations'
        })
      if (errorArchivingSimulations)
        returnValue.push({
          text: errorArchivingSimulations,
          key: 'archivingSimulations'
        })
      if (errorRestoringSimulations)
        returnValue.push({
          text: errorRestoringSimulations,
          key: 'restoringSimulations'
        })
      if (errorEditingSimulation)
        returnValue.push({
          text: errorEditingSimulation,
          key: 'editingSimulation'
        })
      if (errorCreatingSimulation)
        returnValue.push({
          text: errorCreatingSimulation,
          key: 'creatingSimulation'
        })
      if (errorHeroImage)
        returnValue.push({
          text: errorHeroImage,
          key: 'heroImage'
        })
      return returnValue
    },
    getDefaultHeroUrl: ({ defaultHeroUrl }) => {
      return defaultHeroUrl
    },
    getSimulationById:
      ({ activeSimulations, archivedSimulations }) =>
      (simulationId) => {
        return (
          activeSimulations.find((s) => s.id === simulationId) ||
          archivedSimulations.find((s) => s.id === simulationId)
        )
      },
    getHeroImageLoaders: ({ heroImageLoaders }) => {
      return heroImageLoaders
    },
    getSimulationResources({ simulationResources }) {
      return simulationResources
    },
    getPlayer2DBaseUrl: ({ player2DBaseUrl }) => {
      return player2DBaseUrl
    }
  },

  actions: {
    async fetchQuery2D(releaseId, { institutionId = null } = {}) {
      this.query2D = null
      this.player2DBaseUrl = null
      if (!releaseId) return null
      const result = await sas2dApi.get('', {
        params: {
          releaseId,
          ...(institutionId ? { institutionId } : {})
        }
      })

      const resource = result.data.response.resource
      const { r, exp, ui, un, li, ii, si, chars, sig, sas, baseUrl, release } = resource
      const instName = resource['in']
      // Keep sas before sig/release so nested Azure query params stay intact,
      // and always send chars (si backup) so the player skips the access code.
      const params = {
        r,
        exp,
        ui,
        un,
        li,
        ii,
        in: instName,
        si,
        chars: chars || si,
        sas,
        ...(release ? { release } : {}),
        sig
      }
      this.query2D = new URLSearchParams(params).toString()
      this.player2DBaseUrl = baseUrl
      return resource
    },

    async fetchHeroImageArchives() {
      const _indexedDb = await heroIndexedDb.all()
      _.forEachRight(_indexedDb, (h, i) => {
        if (new Date(h.heroExpireTs) < new Date(new Date().getTime() + 15000)) {
          heroIndexedDb.remove(h.id)
          _.pullAt(_indexedDb, i)
        }
      })
      this.heroImageArchives = _.keyBy(_indexedDb, 'id')
    },
    async fetchActiveSimulations(withLoader = true) {
      if (this.errorActiveSimulations != null)
        this.errorActiveSimulations = null
      this.statusActiveSimulations = null
      if (withLoader) this.isLoadingActiveSimulations = true
      try {
        const result = await simulationApi.get('', { params: { active: 1 } })
        const { error, simulationData } = result.data.response
        this.statusActiveSimulations = result.data.status
        if (error) this.errorActiveSimulations = error.message
        else {
          this.activeSimulations = simulationData
          if (this.activeSimulations.length > 0)
            for (const simulation of this.activeSimulations.entries())
              if (_.has(this.heroImageArchives, simulation.id)) {
                simulation.heroUrl =
                  this.heroImageArchives[simulation.id].heroUrl
                simulation.heroExpireTs =
                  this.heroImageArchives[simulation.id].heroExpireTs
              }
        }
      } catch (e) {
        this.errorActiveSimulations =
          e?.response?.data?.response?.error?.message ||
          e.message ||
          String(e) ||
          JSON.stringify(e)
      }
      this.isLoadingActiveSimulations = false
    },
    async fetchArchivedSimulations(withLoader = true) {
      if (this.errorArchivedSimulations != null)
        this.errorArchivedSimulations = null
      this.statusArchivedSimulations = null
      if (withLoader) this.isLoadingArchivedSimulations = true
      try {
        const result = await simulationApi.get('', { params: { active: 0 } })
        const { error, simulationData } = result.data.response
        this.statusArchivedSimulations = result.data.status
        if (error) this.errorArchivedSimulations = error.message
        else {
          this.archivedSimulations = simulationData
          if (this.archivedSimulations.length > 0)
            for (const simulation of this.archivedSimulations.entries())
              if (_.has(this.heroImageArchives, simulation.id)) {
                simulation.heroUrl =
                  this.heroImageArchives[simulation.id].heroUrl
                simulation.heroExpireTs =
                  this.heroImageArchives[simulation.id].heroExpireTs
              }
        }
      } catch (e) {
        this.errorArchivedSimulations =
          e?.response?.data?.response?.error?.message ||
          e.message ||
          String(e) ||
          JSON.stringify(e)
      }
      this.isLoadingArchivedSimulations = false
    },
    async archivingSimulations(simulations) {
      this.isActionMultiple.archivingSimulations = simulations?.length > 1
      this.errorArchivingSimulations = null
      this.isLoadingArchivingSimulations = true
      this.statusArchivingSimulations = null
      const payload = {
        where: {
          id: {
            in: _.map(simulations, ({ id }) => id)
          }
        },
        data: {
          deleteTs: 'softDelete'
        },
        select: {
          id: true,
          title: true,
          description: true,
          heroImage: true,
          estimatedDuration: true,
          author: true,
          deleteTs: true,
          institution: true,
          userData: true,
          simulationProfiles: {
            id: true,
            code: true,
            label: true,
            value: true,
            deleteTs: true
          },
          simulationConcepts: {
            id: true,
            source: true,
            code: true,
            label: true,
            deleteTs: true
          }
        }
      }
      try {
        const result = await simulationApi.patch('', payload)
        const { error, simulationData } = result.data.response
        this.statusArchivingSimulations = result.data.status
        if (error) this.errorArchivingSimulations = error.message
        else {
          if (simulationData.length) {
            _.forEach(simulationData, (s) => {
              if (_.has(s, 'simulationProfiles') && s.simulationProfiles.length)
                _.remove(s.simulationProfiles, (p) => p.deleteTs != null)
              if (_.has(s, 'simulationConcepts') && s.simulationConcepts.length)
                _.remove(s.simulationConcepts, (c) => c.deleteTs != null)
            })
            this.archivedSimulations = [
              ...(this.archivedSimulations ?? []),
              ...simulationData
            ]
            if (
              this.activeSimulations != null &&
              this.activeSimulations.length
            ) {
              const archivedSimulationIds = _.map(simulationData, (s) => s.id)
              _.remove(this.activeSimulations, (s) =>
                archivedSimulationIds.includes(s.id)
              )
            }
          }
        }
      } catch (e) {
        this.errorArchivingSimulations =
          e?.response?.data?.response?.error?.message ||
          e.message ||
          String(e) ||
          JSON.stringify(e)
      }
      this.isLoadingArchivingSimulations = false
      delete this.isActionMultiple.archivingSimulations
    },
    async restoringSimulations(simulations) {
      this.isActionMultiple.restoringSimulations = simulations?.length > 1
      this.errorRestoringSimulations = null
      this.isLoadingRestoringSimulations = true
      this.statusRestoringSimulations = null
      const payload = {
        where: {
          id: {
            in: _.map(simulations, (i) => i.id)
          }
        },
        data: {
          deleteTs: 'restore'
        },
        select: {
          id: true,
          title: true,
          description: true,
          heroImage: true,
          estimatedDuration: true,
          author: true,
          deleteTs: true,
          institution: true,
          userData: true,
          simulationProfiles: {
            id: true,
            code: true,
            label: true,
            value: true,
            deleteTs: true
          },
          simulationConcepts: {
            id: true,
            source: true,
            code: true,
            label: true,
            deleteTs: true
          }
        }
      }
      try {
        const result = await simulationApi.patch('', payload)
        const { error, simulationData } = result.data.response
        this.statusRestoringSimulations = result.data.status
        if (error) this.errorRestoringSimulations = error.message
        else {
          if (simulationData.length) {
            _.forEach(simulationData, (s) => {
              if (_.has(s, 'simulationProfiles') && s.simulationProfiles.length)
                _.remove(s.simulationProfiles, (p) => p.deleteTs != null)
              if (_.has(s, 'simulationConcepts') && s.simulationConcepts.length)
                _.remove(s.simulationConcepts, (c) => c.deleteTs != null)
            })
            this.activeSimulations = [
              ...(this.activeSimulations ?? []),
              ...simulationData
            ]
            if (
              this.archivedSimulations != null &&
              this.archivedSimulations.length
            ) {
              const activeSimulationIds = _.map(simulationData, (s) => s.id)
              _.remove(this.archivedSimulations, (s) =>
                activeSimulationIds.includes(s.id)
              )
            }
          }
        }
      } catch (e) {
        this.errorRestoringSimulations =
          e?.response?.data?.response?.error?.message ||
          e.message ||
          String(e) ||
          JSON.stringify(e)
      }
      this.isLoadingRestoringSimulations = false
      delete this.isActionMultiple.restoringSimulations
    },
    async editingSimulation(simulation) {
      const { id, index } = simulation
      delete simulation.id
      delete simulation.index
      if (!_.isEmpty(simulation)) {
        this.errorEditingSimulation = null
        this.isLoadingEditingSimulation = true
        this.statusEditingSimulation = null
        try {
          if (_.has(simulation, 'simulationProfiles')) {
            const profiles = simulation.simulationProfiles
            if (_.has(profiles, 'archive') && profiles.archive) {
              const payload = {
                where: {
                  id: {
                    in: _.map(profiles.archive, (p) => p.id)
                  }
                },
                data: {
                  deleteTs: 'softDelete'
                },
                select: {
                  id: true,
                  deleteTs: true
                }
              }
              const result = await profilesApi.patch('', payload)
              let { error, simulationProfiles } = result.data.response
              this.statusRestoringSimulations = result.data.status
              if (error) this.errorEditingSimulation = error.message
              else if (
                !_.isArray(simulationProfiles) ||
                simulationProfiles.filter((p) => p.deleteTs != null).length !=
                  profiles.archive.length
              )
                this.errorEditingSimulation =
                  'Error while archiving simulation patient profile(s).'
              else {
                simulationProfiles = simulationProfiles.map(({ id }) => id)
                _.remove(
                  this.activeSimulations[index].simulationProfiles,
                  ({ id }) => simulationProfiles.includes(id)
                )
              }
            }
            if (
              this.errorEditingSimulation == null &&
              _.has(profiles, 'create') &&
              profiles.create
            ) {
              const payload = {
                data: profiles.create,
                select: {
                  id: true,
                  code: true,
                  label: true,
                  value: true,
                  deleteTs: true
                }
              }
              const result = await profilesApi.post('', payload)
              const { error, simulationProfiles } = result.data.response
              this.statusRestoringSimulations = result.data.status
              if (error) this.errorEditingSimulation = error.message
              else if (
                !_.isArray(simulationProfiles) ||
                simulationProfiles.filter((p) => p.deleteTs == null).length !=
                  profiles.create.length
              )
                this.errorEditingSimulation =
                  'Error while creating simulation patient profile(s).'
              else
                this.activeSimulations[index].simulationProfiles = [
                  ...(this.activeSimulations[index].simulationProfiles || []),
                  ...simulationProfiles
                ]
            }
          }
          if (
            this.errorEditingSimulation == null &&
            _.has(simulation, 'simulationConcepts')
          ) {
            const concepts = simulation.simulationConcepts
            if (_.has(concepts, 'archive') && concepts.archive) {
              const payload = {
                where: {
                  id: {
                    in: _.map(concepts.archive, (c) => c.id)
                  }
                },
                data: {
                  deleteTs: 'softDelete'
                },
                select: {
                  id: true,
                  deleteTs: true
                }
              }
              const result = await conceptsApi.patch('', payload)
              let { error, simulationConcepts } = result.data.response
              this.statusRestoringSimulations = result.data.status
              if (error) this.errorEditingSimulation = error.message
              else if (
                !_.isArray(simulationConcepts) ||
                simulationConcepts.filter((c) => c.deleteTs != null).length !=
                  concepts.archive.length
              )
                this.errorEditingSimulation =
                  'Error while archiving simulation concept(s).'
              else {
                simulationConcepts = simulationConcepts.map(({ id }) => id)
                _.remove(
                  this.activeSimulations[index].simulationConcepts,
                  ({ id }) => simulationConcepts.includes(id)
                )
              }
            }
            if (
              this.errorEditingSimulation == null &&
              _.has(concepts, 'create') &&
              concepts.create
            ) {
              const payload = {
                data: concepts.create,
                select: {
                  id: true,
                  source: true,
                  code: true,
                  label: true,
                  deleteTs: true
                }
              }
              const result = await conceptsApi.post('', payload)
              const { error, simulationConcepts } = result.data.response
              this.statusRestoringSimulations = result.data.status
              if (error) this.errorEditingSimulation = error.message
              else if (
                !_.isArray(simulationConcepts) ||
                simulationConcepts.filter((c) => c.deleteTs == null).length !=
                  concepts.create.length
              )
                this.errorEditingSimulation =
                  'Error while creating simulation concept(s).'
              else
                this.activeSimulations[index].simulationConcepts = [
                  ...(this.activeSimulations[index].simulationConcepts || []),
                  ...simulationConcepts
                ]
            }
          }
          if (
            this.errorEditingSimulation == null &&
            _.keys(simulation).every((k) =>
              [
                'simulationProfiles',
                'simulationConcepts',
                'heroImage'
              ].includes(k)
            )
          ) {
            simulation.updateTs = new Date().toISOString()
          }
          if (this.errorEditingSimulation == null) {
            let {
              // eslint-disable-next-line no-unused-vars
              simulationProfiles: _simulationProfiles,
              // eslint-disable-next-line no-unused-vars
              simulationConcepts: _simulationConcepts,
              heroImage,
              ...data
            } = simulation
            //heroImage must be null BUT NOT undefined
            if (heroImage !== undefined) {
              if (heroImage == null) {
                data.heroImage = null
                delete this.heroImageArchives[id]
                await heroIndexedDb.remove(id)
              } else {
                const payload = {
                  container: 'simulations',
                  id,
                  duration: 5,
                  unit: 'minute',
                  files: { heroImage: heroImage.name }
                }
                let result = await blobApi.put('', payload)
                const { error } = result.data.response
                this.statusEditingSimulation = result.data.status
                if (error) this.errorEditingSimulation = error.message
                else {
                  const totalSize = heroImage.size
                  this.heroUploadProgress = 0
                  let totalLoaded = 0
                  const blobClient = new BlockBlobClient(
                    result.data.response.blob.heroImage.url
                  )
                  result = await blobClient.uploadData(heroImage, {
                    blobHTTPHeaders: {
                      blobContentType:
                        heroImage.type || 'application/octet-stream'
                    },
                    onProgress: (event) => {
                      if (event.lengthComputable != null) {
                        totalLoaded += event.loadedBytes
                        this.heroUploadProgress = totalLoaded / totalSize
                      }
                    }
                  })
                  this.statusEditingSimulation = result._response.status
                  this.heroUploadProgress = null
                  const payload = {
                    container: 'simulations',
                    repo: 'SimulationData',
                    id,
                    duration: 24,
                    unit: 'hour',
                    files: { heroImage: heroImage.name }
                  }
                  result = await blobApi.post('', payload)
                  const { error, blob } = result.data.response
                  this.statusEditingSimulation = result.data.status
                  if (error) this.errorEditingSimulation = error.message
                  else {
                    data.heroImage = blob.heroImage.blobName
                    this.activeSimulations[index].heroImage = data.heroImage
                    this.activeSimulations[index].heroUrl = blob.heroImage.url
                    this.activeSimulations[index].heroExpireTs =
                      blob.heroImage.expireTs
                    if (
                      blob.heroImage.url != null &&
                      blob.heroImage.expireTs != null
                    ) {
                      this.heroImageArchives[id] = {
                        heroUrl: blob.heroImage.url,
                        heroExpireTs: blob.heroImage.expireTs
                      }
                      await heroIndexedDb.set(id, {
                        id: simulation.id,
                        heroUrl: blob.heroImage.url,
                        heroExpireTs: blob.heroImage.expireTs
                      })
                    }
                  }
                }
              }
            }
            if (
              _.isObjectLike(data) &&
              !_.isEmpty(data) &&
              this.errorEditingSimulation == null
            ) {
              const payload = {
                where: { id },
                data,
                select: {
                  id: true,
                  title: true,
                  description: true,
                  heroImage: true,
                  estimatedDuration: true,
                  author: true,
                  institution: true,
                  userData: true,
                  simulationProfiles: {
                    id: true,
                    code: true,
                    label: true,
                    value: true,
                    deleteTs: true
                  },
                  simulationConcepts: {
                    id: true,
                    source: true,
                    code: true,
                    label: true,
                    deleteTs: true
                  }
                }
              }
              const result = await simulationApi.patch('', payload)
              const { error, simulationData } = result.data.response
              this.statusEditingSimulation = result.data.status
              if (error) this.errorEditingSimulation = error.message
              else {
                if (_.isArray(simulationData) && simulationData.length == 1) {
                  if (
                    _.has(simulationData[0], 'simulationProfiles') &&
                    simulationData[0].simulationProfiles.length
                  )
                    _.remove(
                      simulationData[0].simulationProfiles,
                      (p) => p.deleteTs != null
                    )
                  if (
                    _.has(simulationData[0], 'simulationConcepts') &&
                    simulationData[0].simulationConcepts.length
                  )
                    _.remove(
                      simulationData[0].simulationConcepts,
                      (c) => c.deleteTs != null
                    )
                  this.activeSimulations[index] = simulationData[0]
                }
              }
            }
          }
          if (this.errorEditingSimulation == null)
            this.setViewSimulation(this.activeSimulations[index])
        } catch (e) {
          this.errorEditingSimulation =
            e?.response?.data?.response?.error?.message ||
            e.message ||
            String(e) ||
            JSON.stringify(e)
        }
        this.isLoadingEditingSimulation = false
      }
    },
    async creatingSimulation(simulation) {
      let {
        title,
        description,
        heroImage,
        estimatedDuration,
        profiles,
        concepts,
        author,
        institution,
        // eslint-disable-next-line no-unused-vars
        resource
      } = simulation
      if (title != null) title = title.replace(/^\s+|\s+$/g, '')
      if (description != null)
        description = description.replace(/^\s+|\s+$/g, '')
      if (profiles != null)
        profiles = profiles.flatMap((p) =>
          Array.isArray(p.value)
            ? p.value.map((v) => ({
                code: p.code,
                label: p.label,
                value: String(v).replace(/^\s+|\s+$/g, '')
              }))
            : [
                {
                  code: p.code,
                  label: p.label,
                  value: String(p.value).replace(/^\s+|\s+$/g, '')
                }
              ]
        )
      if (author != null) author = author.replace(/^\s+|\s+$/g, '')
      this.errorCreatingSimulation = null
      this.isLoadingCreatingSimulation = true
      this.statusCreatingSimulation = null
      let payload = {
        data: {
          title,
          description,
          estimatedDuration,
          author,
          institutionId: institution?.id
        },
        select: {
          id: true,
          title: true,
          description: true,
          estimatedDuration: true,
          author: true,
          institutionId: true,
          userData: {
            id: true,
            provider: true,
            countryCode: true,
            createTs: true,
            updateTs: true,
            deleteTs: true
          }
        }
      }
      if (profiles != null) {
        payload.data['simulationProfiles'] = profiles.map((p) => ({
          code: p.code,
          label: p.label,
          value: p.value
        }))
        payload.select['simulationProfiles'] = {
          id: true,
          code: true,
          label: true,
          value: true,
          deleteTs: true
        }
      }
      if (concepts != null) {
        payload.data['simulationConcepts'] = concepts.map((c) => ({
          source: c.source,
          code: c.code,
          label: c.label
        }))
        payload.select['simulationConcepts'] = {
          id: true,
          source: true,
          code: true,
          label: true,
          deleteTs: true
        }
      }
      try {
        const result = await simulationApi.post('', payload)
        const { error, simulationData } = result.data.response
        this.statusCreatingSimulation = result.data.status
        if (error) this.errorCreatingSimulation = error.message
        else {
          if (
            _.has(simulationData, 'institutionId') &&
            simulationData.institutionId == institution?.id
          )
            simulationData.institution = institution
          if (heroImage) {
            const payload = {
              container: 'simulations',
              id: simulationData.id,
              duration: 5,
              unit: 'minute',
              files: { heroImage: heroImage.name }
            }
            let result = await blobApi.put('', payload)
            const { error } = result.data.response
            this.statusCreatingSimulation = result.data.status
            if (error) this.errorCreatingSimulation = error.message
            else {
              const totalSize = heroImage.size
              this.heroUploadProgress = 0
              let totalLoaded = 0
              const blobClient = new BlockBlobClient(
                result.data.response.blob.heroImage.url
              )
              result = await blobClient.uploadData(heroImage, {
                blobHTTPHeaders: {
                  blobContentType:
                    heroImage.type || 'application/octet-stream'
                },
                onProgress: (event) => {
                  totalLoaded += event.loadedBytes
                  this.heroUploadProgress = totalLoaded / totalSize
                }
              })
              this.statusCreatingSimulation = result._response.status
              this.heroUploadProgress = 0
              const payload = {
                container: 'simulations',
                repo: 'SimulationData',
                id: simulationData.id,
                duration: 24,
                unit: 'hour',
                files: { heroImage: heroImage.name }
              }
              result = await blobApi.post('', payload)
              const { error, blob } = result.data.response
              this.statusEditingSimulation = result.data.status
              if (error) this.errorEditingSimulation = error.message
              else {
                simulationData.heroImage = blob.heroImage.blobName
                simulationData.heroUrl = blob.heroImage.url
                simulationData.heroExpireTs = blob.heroImage.expireTs
                if (
                  blob.heroImage.url != null &&
                  blob.heroImage.expireTs != null
                ) {
                  this.heroImageArchives[simulationData.id] = {
                    heroUrl: blob.heroImage.url,
                    heroExpireTs: blob.heroImage.expireTs
                  }
                  await heroIndexedDb.set(simulationData.id, {
                    id: simulationData.id,
                    heroUrl: blob.heroImage.url,
                    heroExpireTs: blob.heroImage.expireTs
                  })
                }
              }
            }
          }
          this.activeSimulations.unshift(simulationData)
        }
      } catch (e) {
        this.errorCreatingSimulation =
          e?.response?.data?.response?.error?.message ||
          e.message ||
          String(e) ||
          JSON.stringify(e)
      }
      this.isLoadingCreatingSimulation = false
    },
    async getHeroUrl(simulation) {
      if (simulation.heroImage == null) {
        heroIndexedDb.remove(simulation.id)
        delete this.heroImageArchives[simulation.id]
      } else if (
        _.has(this.heroImageArchives, simulation.id) &&
        !this.heroImageLoaders?.[simulation.id]?.loading &&
        new Date(this.heroImageArchives[simulation.id].heroExpireTs) >=
          new Date(new Date().getTime() + 10000)
      ) {
        const { heroUrl, heroExpireTs } = this.heroImageArchives[simulation.id]
        simulation.heroUrl = heroUrl
        simulation.heroExpireTs = heroExpireTs
      } else if (!this.heroImageLoaders?.[simulation.id]?.loading) {
        this.heroImageLoaders[simulation.id] = {
          error: null,
          status: null,
          loading: true
        }
        try {
          const result = await blobApi.get('', {
            params: {
              id: simulation.id,
              container: 'simulations',
              name: simulation.heroImage,
              duration: 7 * 24
            }
          })
          const { error, blob } = result.data.response
          this.heroImageLoaders[simulation.id].status = result.data.status
          if (error) this.heroImageLoaders[simulation.id].error = error.message
          else {
            const { url, expireTs } = blob
            if (url == null || expireTs == null) {
              simulation.heroUrl = null
              simulation.heroExpireTs = null
              delete this.heroImageArchives[simulation.id]
              await heroIndexedDb.remove(simulation.id)
            } else {
              simulation.heroUrl = url
              simulation.heroExpireTs = expireTs
              this.heroImageArchives[simulation.id] = {
                heroUrl: url,
                heroExpireTs: expireTs
              }
              await heroIndexedDb.set(simulation.id, {
                id: simulation.id,
                heroUrl: url,
                heroExpireTs: expireTs
              })
              this.heroImageLoaders[simulation.id].loading = false
            }
          }
        } catch (e) {
          this.heroImageLoaders[simulation.id].error =
            e?.response?.data?.response?.error?.message ||
            e.message ||
            String(e) ||
            JSON.stringify(e)
          this.heroImageLoaders[simulation.id].loading = false
        }
      }
    },
    setFetchActiveSimulationsInterval(newValue) {
      if (newValue == null && this.fetchActiveSimulationsInterval != null)
        clearInterval(this.fetchActiveSimulationsInterval)
      this.fetchActiveSimulationsInterval = newValue
    },
    setFetchArchivedSimulationsInterval(newValue) {
      if (newValue == null && this.fetchArchivedSimulationsInterval != null)
        clearInterval(this.fetchArchivedSimulationsInterval)
      this.fetchArchivedSimulationsInterval = newValue
    },
    formatDuration(estimatedDuration) {
      if (estimatedDuration == null || estimatedDuration == 0) {
        return '0 minutes'
      }
      const secondsInMinute = 60

      let parts = []

      // Calculate Minutes
      const minutes = Math.floor(estimatedDuration / secondsInMinute)
      if (minutes > 0) {
        parts.push(`${minutes} minute${minutes > 1 ? 's' : ''}`)
        estimatedDuration %= secondsInMinute
      }
      // Remaining Seconds
      if (estimatedDuration > 0) {
        parts.push(
          `${estimatedDuration} second${estimatedDuration > 1 ? 's' : ''}`
        )
      }

      if (parts.length == 0) {
        return '0 minutes'
      } else if (parts.length == 1) {
        return parts[0]
      } else if (parts.length == 2) {
        return parts.join(' and ')
      } else {
        const lastPart = parts.pop()
        return `${parts.join(', ')} and ${lastPart}`
      }
    },
    displayProfile(profile, withIndex = true) {
      const getMMdd = (value) => {
        const months = [
          'January',
          'February',
          'March',
          'April',
          'May',
          'June',
          'July',
          'August',
          'September',
          'October',
          'November',
          'December'
        ]
        const month = months[Number(value.slice(0, 2)) - 1]
        let day = String(Number(value.slice(2, 4)))
        day += ['1', '11'].includes(day)
          ? 'st'
          : ['2', '12'].includes(day)
            ? 'nd'
            : ['3', '13'].includes(day)
              ? 'rd'
              : 'th'
        return `${month} ${day}`
      }

      const formatAge = (ageString) => {
        if (typeof ageString !== 'string' || !ageString.trim()) return ''
        const unitsMap = {
          y: 'year',
          m: 'month',
          w: 'week',
          d: 'day'
        }

        const matches = ageString.match(/([ymwd])(\d+)/g)
        if (!matches) return ''
        const parts = matches
          .map((match) => {
            const unit = match[0]
            const value = parseInt(match.slice(1), 10)
            if (!unitsMap[unit] || isNaN(value)) return ''
            const label = unitsMap[unit] + (value !== 1 ? 's' : '')
            return `${value} ${label}`
          })
          .filter(Boolean)
        if (parts.length == 0) return ''
        return parts.length > 1
          ? parts.slice(0, -1).join(', ') + ' and ' + parts[parts.length - 1]
          : parts[0]
      }

      if (!withIndex) _.defaults(profile, this.getProfilesByCode[profile.code])

      return `${profile.label}: ${
        Array.isArray(profile.value)
          ? profile.value.join(', ')
          : profile.type?.constructor == Object && profile.type?.mask == 'MM-DD'
            ? getMMdd(profile.value)
            : profile.code == 'weight'
              ? `${profile.value} kg`
              : profile.code == 'height'
                ? `${profile.value} cm`
                : profile.code == 'age' &&
                    profile.type == 'numbers' &&
                    !Object.hasOwn(profile, '_value')
                  ? formatAge(profile.value)
                  : profile[profile.type == 'numbers' ? '_value' : 'value']
      }`
    },
    setAdminTableData() {
      const {
        data,
        headers,
        itemsPerPage,
        value,
        filterKeys
      } = this.getAdminTabs?.[this.adminTabIndex] || {}
      this.adminTableData = {
        data,
        headers,
        itemsPerPage,
        value,
        filterKeys
      }
    },
    toggleIsViewSimulation() {
      this.isViewSimulation = !this.isViewSimulation
      if (this.viewSimulation != null && !this.isViewSimulation) {
        this.isEditSimulation = false
        this.viewSimulation = null
        this.simulationResources = null
      } else if (this.viewSimulation?._heroUrl == null)
        this.getHeroUrl(this.viewSimulation)
    },

    setIsViewSimulation(newValue) {
      this.isViewSimulation = newValue
      if (this.viewSimulation != null && !this.isViewSimulation)
        this.viewSimulation = null
    },
    activateIsEditSimulation() {
      this.isEdit = true
      this.isEditSimulation = true
    },
    activateIsArchiveSimulation() {
      this.isArchive = true
      this.showDialog = true
    },
    activateIsRestoreSimulation() {
      this.isRestore = true
      this.showDialog = true
    },
    setViewSimulation(newValue, normalizeProfiles = true) {
      if (
        normalizeProfiles &&
        newValue != null &&
        _.has(newValue, 'simulationProfiles') &&
        newValue?.simulationProfiles?.length != null &&
        newValue?.simulationProfiles?.length > 0
      )
        newValue.simulationProfiles = Object.values(
          newValue.simulationProfiles.reduce(
            (a, { id, code, label, value }) => {
              const k = `${code}|${label}`
              if (!a[k]) a[k] = { id, code, label, value }
              else
                a[k].value = Array.isArray(a[k].value)
                  ? [...a[k].value, value]
                  : [a[k].value, value]
              return a
            },
            {}
          )
        )
      this.viewSimulation = newValue
      if (this.viewSimulation != null) {
        for (const k of ['heroUrl', 'heroExpireTs'])
          if (
            this.viewSimulation.id in this.heroImageArchives &&
            k in this.heroImageArchives[this.viewSimulation.id]
          )
            this.viewSimulation[k] =
              this.heroImageArchives[this.viewSimulation.id][k]
        this.viewSimulation._heroUrl =
          this.viewSimulation.heroImage == null ||
          this.heroImageLoaders?.[this.viewSimulation.id]?.error != null ||
          this.heroImageLoaders?.[this.viewSimulation.id]?.loading == true
            ? this.defaultHeroUrl
            : (this.viewSimulation.heroUrl ?? null)
        const resources =
          this.viewSimulation?.simulationResources != null &&
          Array.isArray(this.viewSimulation.simulationResources) &&
          this.viewSimulation.simulationResources.length > 0
            ? this.viewSimulation.simulationResources.map(
                ({ resourceId }) => resourceId
              )
            : null
        this.simulationResources = { id: this.viewSimulation.id, resources }
      } else this.simulationResources = null
    },
    setEditSimulation(newValue) {
      this.editSimulation = newValue
    },
    setSimulationResources(newValue) {
      this.simulationResources = newValue
    },
    setItemsPerPage({ itemsPerPage, value }) {
      this.itemsPerPage[value] = itemsPerPage
    },
    updateSimulationResources(id, resources) {
      const index = _.findIndex(this.activeSimulations, { id })
      if (index != -1) {
        if (resources != null && Array.isArray(resources))
          this.activeSimulations[index].simulationResources = resources
        else if (Object.hasOwn(this.activeSimulations[index], 'simulationResources'))
          delete this.activeSimulations[index].simulationResources
      }
    },
    cancelError(key) {
      switch (key) {
        case 'activeSimulations':
          this.errorActiveSimulations = null
          break
        case 'archivedSimulations':
          this.errorArchivedSimulations = null
          break
        case 'archivingSimulations':
          this.errorArchivingSimulations = null
          break
        case 'restoringSimulations':
          this.errorRestoringSimulations = null
          break
        case 'editingSimulation':
          this.errorEditingSimulation = null
          break
        case 'creatingSimulation':
          this.errorCreatingSimulation = null
          break
        case 'heroImage':
          this.errorHeroImage = null
          break
        default:
          break
      }
    }
  }
})

